import * as React from 'react';
import tw from 'twin.macro';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';

import { ButtonV2, Title } from '@restworld/ui-ds';
import { customImageLoader } from '@restworld/utils-common';
import { useEmployerUserContactData } from '@restworld/feature-authentication';

import { useEmployerUserInformationMutation } from './use-employer-user-information';

export const UploadProfilePicture = () => {
  const { t } = useTranslation('user-settings');
  const ref = React.useRef<HTMLInputElement>(null);
  const { picture_url, user_id } = useEmployerUserContactData();
  const { mutate, isPending } = useEmployerUserInformationMutation();

  const handleFileChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files?.[0]) {
        mutate({ picture: event.target.files[0], id: user_id });
      }
    },
    [mutate, user_id]
  );

  return (
    <UploadLabel htmlFor="upload-image">
      <ImageContainer>
        <Image
          fill
          loader={customImageLoader}
          alt="profile-pic"
          src={picture_url || ''}
          placeholder="empty"
          tw="rounded-lg object-cover object-center"
        />
        <ButtonV2
          variant="tertiary"
          StartIcon={ArrowUpTrayIcon}
          onClick={() => ref.current?.click()}
          size="sm"
          loading={isPending}
          twStyle={tw`bg-white`}
        >
          {t('upload_image')}
        </ButtonV2>
      </ImageContainer>
      <HiddenInput onChange={handleFileChange} type="file" id="picture" accept=".jpg,.png" ref={ref} />
      <Title twStyle={tw`text-neutral-200 text-sm font-medium`}>
        {t('file_format_supported')}: .jpg, .png, etc. Max. file size: 30 MB
      </Title>
    </UploadLabel>
  );
};

const UploadLabel = tw.label`flex-col justify-end items-start gap-4 inline-flex`;
const ImageContainer = tw.div`rounded-lg relative bg-purple-50 flex-col justify-center items-center gap-2.5 flex h-50 w-50`;
const HiddenInput = tw.input`hidden`;
